import Vue from 'vue'
import Vuex from 'vuex'
import { auth } from './auth'
import { ui } from './ui'
import { things } from './things'
import { persons } from './persons'
import { mapState } from './map'
import { events } from './events'
import { tags } from './tags'
import { pointOfInterest } from './pointsofinterest'
import { notifications } from '@/store/notifications'
import {
  GeofencesHelpers,
  PointsHelpers,
  GlobalHelpers
} from '@/helpers/mainExports.js'
import { RootState } from './types'
import { i18n } from '@leapsight/portal'
import { PointsOfInterest } from '@/helpers/objects/PointsOfInterest'

Vue.use(Vuex)
Vue.config.devtools = process.env.NODE_ENV === 'development'

export default new Vuex.Store({
  state: {
    connectionRetryCount: 0,
    user: {
      id: '',
      name: '',
      lastName: '',
      userName: '',
      isAdmin: false,
      isAdminSubFleet: false
    },
    persons: [{}],
    searchInput: '',
    searchMatch: [],
    matchPosition: 1,
    validationsTimeRestriction: null,
    geofences: [],
    pointsOfInterest: [] as PointsOfInterest[],
    filterGeofences: [],
    filterPointsOfInterest: [] as PointsOfInterest[],
    numberCheck: false,
    disconnectDialogValue: false,
    disconnectDialogMessage: '',
    map: {
      setMapTypeId: null
    },
    geofencesOnMap: [],
    pointsOnMap: [],
    pointInLocalStorage: localStorage.getItem('pointsSelected') ?? [],
    showGeofences: false,
    showPoints: false,
    showPoint: false,
    geofenceInLocalStorage: localStorage.getItem('geofencesSelectedFm') ?? [],
    showGeofenceLoadingButton: false,
    showPointLoadingButton: false,
    countryId: localStorage.getItem('country_id') ?? ''
  },
  actions: {
    setSearch({ commit }, searchInput) {
      commit('setSearch', searchInput)
    },
    setSearchMatch({ commit }, payload) {
      commit('setSearchMatch', payload)
    },
    setMatchPosition({ commit }, matchPosition) {
      commit('setMatchPosition', matchPosition)
    },
    filterGeofencesByString({ state, dispatch, commit }, { string }) {
      if (string.length > 1) {
        const filterGeofences = state.geofences.filter(
          (geofence: any) =>
            geofence.properties.name.toLowerCase().match(string) ||
            geofence.agents.some(agent =>
              agent.label.toLowerCase().match(string)
            )
        )
        commit('setFilterGeofencesByString', { filterGeofences })
      } else commit('resetFilterGeofencesState')
    },
    filterPointsByString({ state, commit }, { string }) {
      if (string.length > 1) {
        const filterPointsOfInterest = state.pointsOfInterest.filter((p: any) =>
          p.label.toLowerCase().match(string)
        )
        commit('setFilterPointsByString', { filterPointsOfInterest })
      } else commit('resetFilterPointsState')
    },
    saveGeofenceOnMap({ commit }, { geofenceOnMap }) {
      commit('addGeofencesOnMap', { geofenceOnMap })
    },
    savePointsOnMap({ commit }, { pointOnMap }) {
      commit('addPointsOnMap', { pointOnMap })
    },
    destroyGeofencesOnMap({ commit, getters }) {
      getters.getGeofencesOnMap.map(geofenceOnMap => {
        geofenceOnMap.setMap(null)
      })
      commit('setGeofenceMap', { showGeofencesMap: false })
      commit('destroyGeofencesOnMap')
    },
    destroyPointsOnMap({ commit, getters }) {
      getters.getPointsOnMap.forEach(pointOnMap => {
        pointOnMap.setMap(null)
      })
      commit('setPointsOfInterestMap', { showPointsMap: false })
      commit('destroyPointsOnMap')
    },
    setGeofence({ commit }, { zones }) {
      commit('setGeofences', { zones })
    },
    setPoint({ commit }, { points }) {
      commit('setPoints', { points })
    },
    async showGeofences({ commit, getters, dispatch }, { map, zonesStorage }) {
      commit('setGeofencesInLocalStorage')
      commit('setShowGeofenceLoadingButton')
      if (!getters.showGeofencesOnMap) {
        const promisesGeofencesSelected: any = []

        const zonesStoragePartition = GlobalHelpers.chunkArray(
          zonesStorage,
          200
        )

        for (const zonesStorage of zonesStoragePartition) {
          for (const id of zonesStorage) {
            promisesGeofencesSelected.push(GeofencesHelpers.getGeofence({ id }))
          }

          const results: any = await Promise.allSettled(
            promisesGeofencesSelected
          )
          const zonesSelected = results.map((result: any) => {
            if (result.status === 'fulfilled') {
              const { value } = result
              Object.assign(value.data, { id: value.id })
              if (Boolean(value)) return value.data
              else return {}
            }
          })

          for (const geo of getters.getGeofences) {
            for (const geoSelected of zonesSelected) {
              if (geoSelected.id === geo.id) {
                Object.assign(geo, geoSelected)
              }
            }
          }

          GeofencesHelpers.geofenceOnMap(zonesSelected, map)
        }
      } else {
        dispatch('destroyGeofencesOnMap')
      }
      commit('setShowGeofenceLoadingButton')
    },
    async showPoints({ commit, getters, dispatch }, { map, pointsStorage }) {
      commit('setPointsInLocalStorage')
      commit('setShowPointLoadingButton')

      if (!getters.showPointsOfInterestOnMap) {
        const pointsSelected: any[] = []

        // const pointsStoragePartition = GlobalHelpers.chunkArray(
        //   pointsStorage,
        //   20
        // )

        for (const id of pointsStorage) {
          try {
            const point = await PointsHelpers.getPoint({ id })
            pointsSelected.push({ ...point, id })
          } catch (error) {
            pointsSelected.push({})
          }
        }

        for (const p of getters.getPoints) {
          for (const pointSelected of pointsSelected) {
            if (pointSelected.id === p.id) {
              Object.assign(p, pointSelected)
            }
          }
        }

        const notZoomAllPoints = pointsStorage.length > 1

        PointsHelpers.pointOnMap(pointsSelected, map, notZoomAllPoints)
      } else {
        dispatch('destroyPointsOnMap')
      }

      commit('setShowPointLoadingButton')
    },
    async doAllGeofencesSelected(
      { getters, state, dispatch },
      setGeofencesSelectedFm
    ) {
      const geofencesId = getters.getGeofences.map(geofence => geofence.id)
      localStorage.setItem('geofencesSelectedFm', JSON.stringify(geofencesId))

      try {
        const coordinates = await GeofencesHelpers.getGeofences({
          _p: 'id,data.geometry'
        })

        getters.getGeofences.forEach((geofence, idx) => {
          coordinates.forEach(coordinate => {
            if (geofence.id == coordinate.id) {
              Object.assign(getters.getGeofences[idx], coordinate.data)
              getters.getGeofences[idx].hideGeofence = false
            }
          })
        })

        const geofencesSelectedFm = setGeofencesSelectedFm(
          JSON.parse(JSON.stringify(getters.getGeofences))
        )

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const { map } = mapState.state

        dispatch('destroyGeofencesOnMap')
        GeofencesHelpers.geofenceOnMap(geofencesSelectedFm, map)
      } catch (e) {
        console.error(e)
      }
    },
    async doAllPointsSelected(
      { getters, state, dispatch },
      setPointsSelectedFm
    ) {
      const pointsId = getters.getPoints.map(point => point.id)
      localStorage.setItem('pointsSelected', JSON.stringify(pointsId))

      try {
        const coordinates = await PointsHelpers.getPoints({
          _p: 'id,geometry'
        })

        getters.getPoints.forEach((point, idx) => {
          coordinates.forEach(coordinate => {
            if (point.id == coordinate.id) {
              Object.assign(getters.getPoints[idx], coordinate)
              getters.getPoints[idx].hidePoint = false
            }
          })
        })

        const pointsSelectedFm = setPointsSelectedFm(
          JSON.parse(JSON.stringify(getters.getPoints))
        )

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const { map } = mapState.state

        dispatch('destroyPointsOnMap')
        PointsHelpers.pointOnMap(pointsSelectedFm, map)
      } catch (e) {
        console.error(e)
      }
    },
    async deletePoint({ dispatch, commit }, pointId) {
      try {
        await PointsHelpers.deletePoint(pointId)
        commit('removePointById', pointId)
      } catch (error) {
        dispatch('setResponseError', true)
        const response = i18n.t(
          'fleet_management.points_of_interest.data_table.errors.no_info'
        )
        dispatch('setResponseMessage', response)
      }
    },
    setResponseError({ commit }, payload) {
      commit('setResponseError', payload)
    },
    setResponseMessage({ commit }, message) {
      commit('setResponseMessage', message)
    }
  },
  mutations: {
    removePointById(state: RootState, pointId: number) {
      state.pointsOfInterest = state.pointsOfInterest.filter(
        point => point.id !== pointId
      )
      state.filterPointsOfInterest = state.pointsOfInterest
    },
    setMapType(state, payload) {
      let map: any
      let type
      if (payload.hasOwnProperty('map')) map = payload.map
      else map = state.map

      if (payload.showSatelliteMap) type = 'hybrid'
      else type = 'roadmap'
      map.setMapTypeId(type)
    },
    setShowGeofenceLoadingButton(state) {
      state.showGeofenceLoadingButton = !state.showGeofenceLoadingButton
    },
    setShowPointLoadingButton(state) {
      state.showPointLoadingButton = !state.showPointLoadingButton
    },
    setDisconnectDialogValue(state, payload) {
      state.disconnectDialogValue = payload
    },
    setDisconnectDialogMessage(state, payload) {
      state.disconnectDialogMessage = payload
    },
    setNumberCheck(state, payload) {
      state.numberCheck = payload
    },
    validationsTimeRestriction(state, payload) {
      state.validationsTimeRestriction = payload
    },
    setSearch(state, payload) {
      state.searchInput = payload
    },
    setSearchMatch(state, payload) {
      state.searchMatch = payload
    },
    setMatchPosition(state, matchPosition) {
      state.matchPosition = matchPosition
    },
    setUser(state, user) {
      state.user = user
    },
    setGeofences(state, { zones }) {
      state.geofences = zones
    },
    setPoints(state, { points }) {
      state.pointsOfInterest = points
    },
    resetFilterGeofencesState(state) {
      state.filterGeofences = state.geofences
    },
    resetFilterPointsState(state) {
      state.filterPointsOfInterest = state.pointsOfInterest
    },
    setFilterGeofencesByString(state, { filterGeofences }) {
      state.filterGeofences = filterGeofences
    },
    setFilterPointsByString(state, { filterPoints }) {
      state.filterPointsOfInterest = filterPoints
    },
    destroyGeofencesOnMap(state) {
      state.geofencesOnMap = []
    },
    destroyPointsOnMap(state) {
      state.pointsOnMap = []
    },
    setGeofencesInLocalStorage(state) {
      state.geofenceInLocalStorage =
        JSON.parse(
          JSON.stringify(localStorage.getItem('geofencesSelectedFm'))
        ) ?? []
    },
    setPointsInLocalStorage(state) {
      state.pointInLocalStorage =
        JSON.parse(JSON.stringify(localStorage.getItem('pointsSelected'))) ?? []
    },
    setGeofenceMap(state, { showGeofencesMap }) {
      state.showGeofences = showGeofencesMap
    },
    setPointsOfInterestMap(state, { showPointsMap }) {
      state.showPoints = showPointsMap
    },
    addGeofencesOnMap(state, { geofenceOnMap }) {
      // @ts-ignore
      state.geofencesOnMap.push(geofenceOnMap)
    },
    addPointsOnMap(state, { pointOnMap }) {
      // @ts-ignore
      state.pointsOnMap.push(pointOnMap)
    },
    updatePointByIndex(state, { idx, data }) {
      Object.assign(state.filterPointsOfInterest[idx], data)
    }
  },
  getters: {
    map: state => state.map,
    getUserId: state => state.user.id,
    getGeofenceInLocalStorage: state => state.geofenceInLocalStorage,
    getPointsInLocalStorage: state => state.pointInLocalStorage,
    isAdmin: state => state.user.isAdmin,
    getCountryId: state => state.countryId,
    isAdminSubFleet: state => state.user.isAdminSubFleet,
    validationsTimeRestriction(state) {
      return state.validationsTimeRestriction
    },
    getSearchInput: state => {
      return state.searchInput
    },
    getSearchMatch: state => {
      return state.searchMatch
    },
    getMatchPosition: state => {
      return state.matchPosition
    },
    getGeofences: state => {
      return state.filterGeofences
    },
    getPoints: state => {
      return state.pointsOfInterest
    },
    getGeofencesSortedByName(state) {
      return state.filterGeofences.sort(
        (geoA: { properties }, geoB: { properties }) =>
          geoA.properties.name > geoB.properties.name
            ? 1
            : geoA.properties.name < geoB.properties.name
            ? -1
            : 0
      )
    },
    getPointsSortedByName(state) {
      return state.pointsOfInterest.sort((pA: { label }, pB: { label }) =>
        pA.label > pB.label ? 1 : pA.label < pB.label ? -1 : 0
      )
    },
    getGeofencesOnMap: state => state.geofencesOnMap,
    getPointsOnMap: state => state.pointsOnMap,
    showGeofence: state => state.showGeofences,
    showPoint: state => state.showPoints,
    showGeofencesOnMap: state => state.geofencesOnMap.length > 0,
    showPointsOfInterestOnMap: state => state.pointsOnMap.length > 0,
    filteredPointsOfInterest: state => {
      if (!state.searchInput || state.searchInput.trim().length < 2) {
        return state.pointsOfInterest
      }
      const search = state.searchInput.toLowerCase()
      return state.pointsOfInterest.filter(point =>
        point.label.toLowerCase().includes(search)
      )
    }
  },
  modules: {
    auth,
    ui,
    things,
    persons,
    notifications,
    mapState,
    events,
    tags,
    pointOfInterest
  }
})
